import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import css from './CheckoutCompletePage.css';
import PageWrapper from '../../custom-components/wrappers/page-wrapper/PageWrapper';
import { IconSpinner } from '../../components';
import { buildDefaultAvailabilityData } from '../../custom-ducks/settings.duck';
import { getBearerToken } from '../../util/cookie';
import config from '../../config';

// Account creation here happens server-side (completeCheckoutSignup.js),
// which has no browser to read a timezone from, so the "browser local
// timezone" default availability is set from this page instead, right
// after the auth cookie lands. The token was just minted by the server
// so it can't be expired yet — getBearerToken's fast (non-refresh) path
// covers this without needing a full sdk instance here.
function saveDefaultAvailability() {
  getBearerToken(null)
    .then(authHeader => {
      if (!authHeader) return;
      return axios.post(
        `${config.coachSaaSService}settings/calendarAvailability`,
        { data: buildDefaultAvailabilityData() },
        { headers: { 'content-type': 'application/json', Authorization: authHeader } }
      );
    })
    .catch(() => {});
}

const MAX_RETRIES = 4;
const RETRY_DELAY_MS = 2500;

// Coach lands here straight from Chargebee after a direct hosted-checkout
// link (no prior Viveka account) — see server/api/auth/completeCheckoutSignup.js
// for the account-creation + auto-login logic this page triggers. Also
// handles the ?subscriptionId= variant from GetStartedPage2's on-page
// popup checkout (Chargebee Card Component) — completeCheckoutSignup.js
// already supports verifying either reference.
function CheckoutCompletePage({ location }) {
  const [state, setState] = useState({ status: 'loading' });
  const attemptRef = useRef(0);

  const searchParams = new URLSearchParams((location && location.search) || '');
  const hostedPageId = searchParams.get('id');
  const subscriptionId = searchParams.get('subscriptionId');

  // Shared by the initial mount and the "Check again" button. `allowRetries`
  // gates the automatic retry loop — the manual button always makes exactly
  // one attempt instead of silently re-arming a timer chain.
  function runCheck(allowRetries, isCancelledRef) {
    axios
      .post('/api/auth/complete-checkout-signup', hostedPageId ? { hostedPageId } : { subscriptionId })
      .then(res => {
        if (isCancelledRef.current) return;
        const data = res.data || {};
        // DEBUG: server-side step trace, safe to remove once this flow is
        // confirmed stable — see completeCheckoutSignup.js's `trace()` calls.
        // eslint-disable-next-line no-console
        console.log('complete-checkout-signup steps:', data.debug);
        if (data.ok) {
          setState({ status: 'success' });
          saveDefaultAvailability();
          window.location.href = '/v-coach/dashboard';
          return;
        }
        if (data.reason === 'existing-account') {
          setState({ status: 'existing' });
          window.location.href = `/login?email=${encodeURIComponent(data.email || '')}`;
          return;
        }
        if (allowRetries && data.reason === 'not-confirmed' && attemptRef.current < MAX_RETRIES) {
          attemptRef.current += 1;
          setTimeout(() => {
            if (!isCancelledRef.current) runCheck(true, isCancelledRef);
          }, RETRY_DELAY_MS);
          return;
        }
        setState({ status: 'pending' });
      })
      .catch(err => {
        // DEBUG: same step trace, read from the error response when the
        // server returned a non-2xx status (e.g. 500/502).
        // eslint-disable-next-line no-console
        console.log(
          'complete-checkout-signup steps (error response):',
          err && err.response && err.response.data && err.response.data.debug
        );
        if (!isCancelledRef.current) setState({ status: 'error' });
      });
  }

  useEffect(() => {
    if (!hostedPageId && !subscriptionId) {
      setState({ status: 'error' });
      return;
    }

    const cancelledRef = { current: false };
    runCheck(true, cancelledRef);

    return () => {
      cancelledRef.current = true;
    };
  }, [hostedPageId, subscriptionId]);

  const handleRetry = () => {
    attemptRef.current = 0;
    setState({ status: 'loading' });
    runCheck(false, { current: false });
  };

  return (
    <PageWrapper>
      <div className={css.base}>
        <div className={css.card}>
          {(state.status === 'loading' || state.status === 'success' || state.status === 'existing') && (
            <>
              <div className={css.spinnerWrap}>
                <IconSpinner />
              </div>
              <p className={css.title}>Confirming your payment&hellip;</p>
              <p className={css.body}>This will only take a second.</p>
            </>
          )}

          {state.status === 'pending' && (
            <>
              <div className={css.iconBig}>&#10003;</div>
              <h1 className={css.title}>Almost there</h1>
              <p className={css.body}>
                We're still waiting on confirmation from Chargebee. This can take a few extra
                seconds after payment &mdash; try checking again.
              </p>
              <div className={css.actions}>
                <button type="button" className={css.btnPrimary} onClick={handleRetry}>
                  Check again
                </button>
              </div>
            </>
          )}

          {state.status === 'error' && (
            console.log('CheckoutCompletePage: error state', state),
            <>
              <div className={css.iconBigError}>!</div>
              <h1 className={css.title}>Something went wrong</h1>
              <p className={css.body}>
                We couldn't confirm your payment automatically. Your payment is safe &mdash; please
                reply to your Chargebee receipt email or contact support and we'll get your account
                set up.
              </p>
            </>
          )}

          <div className={css.footer}>
            <span className={css.brand}>
              Viveka<span className={css.brandDot}>.</span>
            </span>
            <span className={css.tagline}>The infrastructure for human development.</span>
          </div>
        </div>
      </div>
    </PageWrapper>
  );
}

export default compose(withRouter)(CheckoutCompletePage);
